import React from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { useCompanyAddChargePointToCompanyMutation } from '../../common/redux/api/web-api'
import BootstrapModalLayout from '../../ui/BootstrapModalLayout'
import { RootState } from '../redux/store'
import Input from '../../common/components/Input'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import useText from '../../common/hooks/useText'

interface IAddChargePointModal {
    close: () => void
    cancel: () => void
}

type Inputs = {
    id: string
    wifiPassword: string
}

export const AddChargePointModal = React.memo(function AddChargePointModal(
    props: IAddChargePointModal
): React.ReactElement {
    const text = useText()
    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<Inputs>({ mode: 'onChange', delayError: 500 })

    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const [addChargePoint, { isLoading }] = useCompanyAddChargePointToCompanyMutation()

    const doAddChargePoint = async (formValues: Inputs) => {
        try {
            await addChargePoint({
                companyId,
                chargePointCredentials: formValues,
            }).unwrap()

            props.close()
            notifySuccess(text('lblChargerWasAdded'))
        } catch (e) {
            notifyError(text('msgChargerFailedToAdd'))
        }
    }

    return (
        <BootstrapModalLayout
            title={text('lblChargerAddingModal')}
            data-testid="add-chargepoint-modal"
            actions={[
                {
                    label: text('btnCancel'),
                    callback: props.cancel,
                    btn: 'btn-secondary',
                },
                {
                    label: text('btnSave'),
                    callback: handleSubmit(doAddChargePoint),
                    disabled: !isValid || isLoading,
                    loading: isLoading,
                },
            ]}
        >
            <form onSubmit={handleSubmit(doAddChargePoint)} className="flex flex-column gap-4">
                <Input
                    id="id"
                    labelText={text('lblSerialNumber')}
                    data-testid="serial-number-input"
                    {...register('id', { required: true })}
                    error={errors.id?.message}
                    required
                />
                <Input
                    id="wifiPassword"
                    data-testid="pin-input"
                    labelText={text('lblWifiPasswordOrPinCodeField')}
                    {...register('wifiPassword', { required: true })}
                    error={errors.wifiPassword?.message}
                    required
                />
            </form>
        </BootstrapModalLayout>
    )
})
